import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronRight, GitBranch, ChevronDown, ChevronUp, X, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

// ── Types ────────────────────────────────────────────────────────────────────

type TripType = 'first_mile' | 'transfer' | 'last_mile' | 'direct';
type TransportMode = 'land' | 'sea' | 'air' | 'rail';
type EndpointKind = 'node' | 'role';
type NodeRole = 'origin_warehouse' | 'customer_address';

interface LegEndpoint {
  kind: EndpointKind;
  node_id: string;
  role: NodeRole | '';
}

interface LegDraft {
  trip_type: TripType | '';
  transport_mode: TransportMode | '';
  from: LegEndpoint;
  to: LegEndpoint;
  estimated_transit_hours: string;
}

interface NodeOption {
  id: string;
  name: string;
  type: string;
  is_system: boolean; // tenant_id=null AND carrier_id=null
}

interface TemplateLeg {
  id: string;
  sequence: number;
  trip_type: TripType;
  transport_mode: TransportMode;
  from_node_id?: string;
  from_node_role?: string;
  to_node_id?: string;
  to_node_role?: string;
  estimated_transit_hours?: number;
}

interface RouteTemplate {
  id: string;
  name: string;
  description?: string;
  estimated_total_hours?: number;
  is_active: boolean;
  legs?: TemplateLeg[];
}

// ── Labels ───────────────────────────────────────────────────────────────────

const TRIP_TYPE_LABELS: Record<TripType, string> = {
  first_mile: 'أولى',
  transfer: 'نقل',
  last_mile: 'أخيرة',
  direct: 'مباشرة',
};

const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  land: 'بري',
  sea: 'بحري',
  air: 'جوي',
  rail: 'سكة حديد',
};

const NODE_ROLE_LABELS: Record<NodeRole, string> = {
  origin_warehouse: 'مستودع المصدر',
  customer_address: 'عنوان العميل',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function emptyLeg(): LegDraft {
  return {
    trip_type: '',
    transport_mode: '',
    from: { kind: 'node', node_id: '', role: '' },
    to: { kind: 'node', node_id: '', role: '' },
    estimated_transit_hours: '',
  };
}

function defaultForm() {
  return {
    name: '',
    description: '',
    estimated_total_hours: '',
    is_active: true,
  };
}

// ── LegRow ───────────────────────────────────────────────────────────────────

interface LegRowProps {
  index: number;
  leg: LegDraft;
  onChange: (index: number, leg: LegDraft) => void;
  onRemove: (index: number) => void;
  nodes: NodeOption[];
}

function EndpointFields({
  label,
  endpoint,
  onChange,
  nodes,
}: {
  label: string;
  endpoint: LegEndpoint;
  onChange: (ep: LegEndpoint) => void;
  nodes: NodeOption[];
}) {
  const carrierNodes = nodes.filter((n) => !n.is_system);
  const systemNodes = nodes.filter((n) => n.is_system);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <RadioGroup
        value={endpoint.kind}
        onValueChange={(v) => onChange({ ...endpoint, kind: v as EndpointKind, node_id: '', role: '' })}
        className="flex gap-4"
      >
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="node" id={`node-${label}`} />
          <Label htmlFor={`node-${label}`} className="text-xs cursor-pointer">عقدة محددة</Label>
        </div>
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="role" id={`role-${label}`} />
          <Label htmlFor={`role-${label}`} className="text-xs cursor-pointer">دور</Label>
        </div>
      </RadioGroup>
      {endpoint.kind === 'node' ? (
        <Select
          value={endpoint.node_id}
          onValueChange={(v) => onChange({ ...endpoint, node_id: v })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue placeholder="اختر العقدة" />
          </SelectTrigger>
          <SelectContent>
            {carrierNodes.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">عقد الناقل</div>
                {carrierNodes.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    {n.name} <span className="text-muted-foreground text-xs">({n.type})</span>
                  </SelectItem>
                ))}
              </>
            )}
            {systemNodes.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">عقد البنية التحتية</div>
                {systemNodes.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    {n.name} <span className="text-muted-foreground text-xs">({n.type})</span>
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
      ) : (
        <Select
          value={endpoint.role}
          onValueChange={(v) => onChange({ ...endpoint, role: v as NodeRole })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue placeholder="اختر الدور" />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(NODE_ROLE_LABELS) as NodeRole[]).map((r) => (
              <SelectItem key={r} value={r}>{NODE_ROLE_LABELS[r]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function LegRow({ index, leg, onChange, onRemove, nodes }: LegRowProps) {
  const update = (partial: Partial<LegDraft>) => onChange(index, { ...leg, ...partial });

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">الساق {index + 1}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={() => onRemove(index)}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">نوع الرحلة</Label>
          <Select
            value={leg.trip_type}
            onValueChange={(v) => update({ trip_type: v as TripType })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="اختر" />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(TRIP_TYPE_LABELS) as TripType[]).map((t) => (
                <SelectItem key={t} value={t}>{TRIP_TYPE_LABELS[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">وسيلة النقل</Label>
          <Select
            value={leg.transport_mode}
            onValueChange={(v) => update({ transport_mode: v as TransportMode })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="اختر" />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(TRANSPORT_MODE_LABELS) as TransportMode[]).map((m) => (
                <SelectItem key={m} value={m}>{TRANSPORT_MODE_LABELS[m]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <EndpointFields
          label="نقطة الانطلاق"
          endpoint={leg.from}
          onChange={(ep) => update({ from: ep })}
          nodes={nodes}
        />
        <EndpointFields
          label="نقطة الوصول"
          endpoint={leg.to}
          onChange={(ep) => update({ to: ep })}
          nodes={nodes}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">وقت العبور التقديري (ساعات)</Label>
        <Input
          type="number"
          min="0"
          step="0.5"
          placeholder="اختياري"
          value={leg.estimated_transit_hours}
          onChange={(e) => update({ estimated_transit_hours: e.target.value })}
          className="h-8 text-sm"
        />
      </div>
    </div>
  );
}

// ── TemplateCard ─────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: RouteTemplate;
  onDelete: (id: string) => void;
  onEdit: (template: RouteTemplate) => void;
}

function TemplateCard({ template, onDelete, onEdit }: TemplateCardProps) {
  const [expanded, setExpanded] = useState(false);
  const legs = template.legs ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium truncate">{template.name}</span>
              <Badge variant={template.is_active ? 'default' : 'secondary'}>
                {template.is_active ? 'نشط' : 'غير نشط'}
              </Badge>
            </div>
            <div className="flex gap-4 mt-1 text-sm text-muted-foreground">
              {template.estimated_total_hours != null && (
                <span>{template.estimated_total_hours} ساعة</span>
              )}
              <span>{legs.length} ساق</span>
            </div>
          </div>
          <div className="flex items-center gap-2 mr-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); onEdit(template); }}
              title="تعديل"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(template.id); }}
              title="حذف"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {expanded && legs.length > 0 && (
          <div className="border-t px-4 pb-4 pt-3 space-y-2">
            {legs.map((leg) => (
              <div key={leg.id} className="flex gap-3 text-sm border rounded p-2 bg-muted/20">
                <span className="text-muted-foreground w-4 shrink-0">{leg.sequence}</span>
                <div className="space-y-0.5">
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">
                      {TRIP_TYPE_LABELS[leg.trip_type]}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {TRANSPORT_MODE_LABELS[leg.transport_mode]}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    من:{' '}
                    {leg.from_node_role
                      ? NODE_ROLE_LABELS[leg.from_node_role as NodeRole] ?? leg.from_node_role
                      : leg.from_node_id ?? '-'}
                    {' → '}
                    إلى:{' '}
                    {leg.to_node_role
                      ? NODE_ROLE_LABELS[leg.to_node_role as NodeRole] ?? leg.to_node_role
                      : leg.to_node_id ?? '-'}
                  </div>
                  {leg.estimated_transit_hours != null && (
                    <div className="text-xs text-muted-foreground">
                      {leg.estimated_transit_hours} ساعة
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {expanded && legs.length === 0 && (
          <div className="border-t px-4 py-3 text-sm text-muted-foreground text-center">
            لا توجد أساق في هذا القالب
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function templateLegToDraft(leg: TemplateLeg): LegDraft {
  return {
    trip_type: leg.trip_type,
    transport_mode: leg.transport_mode,
    from: leg.from_node_role
      ? { kind: 'role', node_id: '', role: leg.from_node_role as NodeRole }
      : { kind: 'node', node_id: leg.from_node_id ?? '', role: '' },
    to: leg.to_node_role
      ? { kind: 'role', node_id: '', role: leg.to_node_role as NodeRole }
      : { kind: 'node', node_id: leg.to_node_id ?? '', role: '' },
    estimated_transit_hours: leg.estimated_transit_hours?.toString() ?? '',
  };
}

export default function RouteTemplatesPage() {
  const navigate = useNavigate();
  const { get, post, patch, delete: del, loading } = useApi();
  const { user } = useAuth();

  const [templates, setTemplates] = useState<RouteTemplate[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RouteTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState(defaultForm());
  const [legs, setLegs] = useState<LegDraft[]>([]);
  const [nodes, setNodes] = useState<NodeOption[]>([]);

  const carrierId = user?.carrier_id;

  const fetchTemplates = useCallback(async () => {
    if (!carrierId) return;
    try {
      const res: any = await get(`/api/v1/carriers/${carrierId}/route-templates`);
      setTemplates(res?.list ?? res?.data ?? res ?? []);
    } catch {
      toast.error('تعذر تحميل قوالب المسارات');
    } finally {
      setHasFetched(true);
    }
  }, [carrierId, get]);

  useEffect(() => {
    fetchTemplates();
    if (!carrierId) return;
    // Load carrier nodes + system nodes (tenant_id=null, carrier_id=null)
    Promise.all([
      get(`/api/v1/dashboard/nodes?carrier_id=${carrierId}`),
      get('/api/v1/dashboard/nodes'),
    ]).then(([carrierRes, systemRes]: any[]) => {
      const HUB_TYPES = ['fulfillment_center', 'hub'];
      const carrierList: NodeOption[] = (Array.isArray(carrierRes) ? carrierRes : carrierRes?.data ?? [])
        .filter((n: any) => n.carrier_id === carrierId && HUB_TYPES.includes(n.type))
        .map((n: any) => ({ id: n.id, name: n.name, type: n.type, is_system: false }));
      const systemList: NodeOption[] = (Array.isArray(systemRes) ? systemRes : systemRes?.data ?? [])
        .filter((n: any) => !n.carrier_id && !n.tenant_id)
        .map((n: any) => ({ id: n.id, name: n.name, type: n.type, is_system: true }));
      setNodes([...carrierList, ...systemList]);
    }).catch(() => {});
  }, [fetchTemplates, carrierId]);

  const handleEdit = (template: RouteTemplate) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      description: template.description ?? '',
      estimated_total_hours: template.estimated_total_hours?.toString() ?? '',
      is_active: template.is_active,
    });
    setLegs((template.legs ?? []).map(templateLegToDraft));
    setShowCreate(true);
  };

  const handleDelete = async (id: string) => {
    if (!carrierId) return;
    try {
      await del(`/api/v1/carriers/${carrierId}/route-templates/${id}`);
      toast.success('تم حذف القالب بنجاح');
      fetchTemplates();
    } catch {
      toast.error('تعذر حذف القالب');
    }
  };

  const handleLegChange = (index: number, leg: LegDraft) => {
    setLegs((prev) => prev.map((l, i) => (i === index ? leg : l)));
  };

  const handleLegRemove = (index: number) => {
    setLegs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!carrierId) return;
    if (!form.name.trim()) {
      toast.error('اسم القالب مطلوب');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        estimated_total_hours: form.estimated_total_hours
          ? parseFloat(form.estimated_total_hours)
          : undefined,
        is_active: form.is_active,
        legs: legs.map((leg, idx) => ({
          sequence: idx + 1,
          trip_type: leg.trip_type || undefined,
          transport_mode: leg.transport_mode || undefined,
          from_node_id: leg.from.kind === 'node' ? leg.from.node_id || undefined : undefined,
          from_node_role: leg.from.kind === 'role' ? leg.from.role || undefined : undefined,
          to_node_id: leg.to.kind === 'node' ? leg.to.node_id || undefined : undefined,
          to_node_role: leg.to.kind === 'role' ? leg.to.role || undefined : undefined,
          estimated_transit_hours: leg.estimated_transit_hours
            ? parseFloat(leg.estimated_transit_hours)
            : undefined,
        })),
      };
      if (editingTemplate) {
        await patch(`/api/v1/carriers/${carrierId}/route-templates/${editingTemplate.id}`, payload);
        toast.success('تم تحديث القالب بنجاح');
      } else {
        await post(`/api/v1/carriers/${carrierId}/route-templates`, payload);
        toast.success('تم إنشاء القالب بنجاح');
      }
      setShowCreate(false);
      setEditingTemplate(null);
      setForm(defaultForm());
      setLegs([]);
      fetchTemplates();
    } catch {
      toast.error('تعذر إنشاء القالب');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <PageContainer>
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">قوالب المسارات</h1>
                <p className="text-sm text-muted-foreground">إدارة قوالب مسارات التوصيل</p>
              </div>
            </div>
            <Button onClick={() => setShowCreate(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة قالب
            </Button>
          </div>
        </div>

        {/* Content */}
        {(!hasFetched || loading) ? (
          <LoadingSkeleton type="card" rows={4} />
        ) : templates.length === 0 ? (
          <EmptyState
            icon={<GitBranch className="h-16 w-16" />}
            title="لا توجد قوالب مسارات"
            description="أضف قالب مسار لتنظيم رحلات التوصيل"
            action={{ label: 'إضافة قالب', onClick: () => setShowCreate(true) }}
          />
        ) : (
          <div className="space-y-3">
            {templates.map((t) => (
              <TemplateCard key={t.id} template={t} onDelete={handleDelete} onEdit={handleEdit} />
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={(open) => {
          if (!open) {
            setForm(defaultForm());
            setLegs([]);
            setEditingTemplate(null);
          }
          setShowCreate(open);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'تعديل القالب' : 'إضافة قالب مسار جديد'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Name */}
              <div className="space-y-2">
                <Label>اسم القالب <span className="text-destructive">*</span></Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="مثال: مسار الرياض - جدة"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>الوصف</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="وصف اختياري للقالب"
                  rows={2}
                />
              </div>

              {/* Estimated Hours */}
              <div className="space-y-2">
                <Label>وقت التسليم التقديري (ساعات)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.estimated_total_hours}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, estimated_total_hours: e.target.value }))
                  }
                  placeholder="اختياري"
                />
              </div>

              {/* Active */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: !!v }))}
                />
                <Label htmlFor="is_active" className="cursor-pointer">نشط</Label>
              </div>

              {/* Legs */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">الأساق</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLegs((prev) => [...prev, emptyLeg()])}
                    className="gap-1.5 h-8"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    إضافة ساق
                  </Button>
                </div>

                {legs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                    لا توجد أساق. اضغط "إضافة ساق" لإضافة الأولى.
                  </p>
                )}

                {legs.map((leg, idx) => (
                  <LegRow
                    key={idx}
                    index={idx}
                    leg={leg}
                    onChange={handleLegChange}
                    onRemove={handleLegRemove}
                    nodes={nodes}
                  />
                ))}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreate(false);
                  setEditingTemplate(null);
                  setForm(defaultForm());
                  setLegs([]);
                }}
                disabled={submitting}
              >
                إلغاء
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'جارٍ الحفظ...' : 'حفظ القالب'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </div>
  );
}
